import React, { type FunctionComponent } from 'react';

import { mdiLogout } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Tab, Tabs, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import AdminSettingsContainer from './AdminSettingsContainer';
import { PortalTabs } from './config/portalConfig';
import { getUrls } from '../../utils/url';

const NavContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-column: 1 / 2;
    grid-row: 2 / 3;
    padding: 1rem;
    background-color: ${(props) => props.theme.colors.white};
`;

const BottomControls = styled.div`
    width: 100%;
    margin-top: auto;
`;

const StyledTabs = styled(Tabs)`
    width: 100%;
`;

const StyledTab = styled(Tab)`
    font-size: 14px;
    text-transform: none;
    align-items: start;
`;

const StyledButton = styled(Button)`
    color: ${(props) => props.theme.colors.secondary.lighter};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    font-size: 14px;

    svg {
        width: 24px;
        margin-right: 0.5rem;
        color: inherit;
    }
`;

const PortalNav: FunctionComponent = () => {
    const { activeTab, setActiveTab, adminSettings } = useContainer(AdminSettingsContainer);
    const { isSuperAdmin, isAdmin, isInstructor, authMode, hasCustomerSupportMode } = useContainer(AdminAuthContainer);
    const { t } = useTranslation('portal', { useSuspense: false });

    const history = useHistory();
    const { path } = useRouteMatch();

    const handleChange = (value) => {
        setActiveTab(value);
        history.push(`${path}${value}`);
    };

    const logout = () => {
        const { vanityUrl } = getUrls();

        localStorage.removeItem('user_uuid');
        window.location.href = vanityUrl;
    };

    let isAuthModeValid = false;
    if (authMode === 'student') {
        isAuthModeValid = true;
    }
    if (authMode === 'instructor') {
        if (isInstructor || isAdmin || isSuperAdmin) {
            isAuthModeValid = true;
        }
    }
    if (authMode === 'admin') {
        if (isAdmin || isSuperAdmin) {
            isAuthModeValid = true;
        }
    }
    if (authMode === 'customerSupport') {
        if (isSuperAdmin || hasCustomerSupportMode) {
            isAuthModeValid = true;
        }
    }
    if (authMode === 'classBusiness') {
        if (isSuperAdmin) {
            isAuthModeValid = true;
        }
    }
    if (authMode === 'customerBusiness') {
        if (isAdmin || isSuperAdmin) {
            isAuthModeValid = true;
        }
    }

    if (!isAuthModeValid) {
        return <Box>{t('invalid-auth-mode')}</Box>;
    }
    return (
        <NavContainer>
            {authMode === 'admin' && adminSettings.featureSettings.dataPortalAdmin && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Users} label={t('navigationTabs.users')} />
                </StyledTabs>
            )}
            {authMode === 'instructor' && adminSettings.featureSettings.dataPortalInstructor && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Profile} label={t('navigationTabs.profile')} />
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Users} label={t('navigationTabs.users')} />
                    <StyledTab value={PortalTabs.Planner} label={t('navigationTabs.planner')} />
                    <StyledTab value={PortalTabs.Insights} label={t('navigationTabs.insights')} />
                    <StyledTab value={PortalTabs.Settings} label={t('navigationTabs.settings')} />
                    <StyledTab value={PortalTabs.Feedback} label={t('navigationTabs.feedback')} />
                    <StyledTab value={PortalTabs.Support} label={t('navigationTabs.support')} />
                </StyledTabs>
            )}
            {authMode === 'student' && adminSettings.featureSettings.dataPortalStudent && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Profile} label={t('navigationTabs.profile')} />
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Insights} label={t('navigationTabs.insights')} />
                    <StyledTab value={PortalTabs.Settings} label={t('navigationTabs.settings')} />
                </StyledTabs>
            )}
            {authMode === 'customerSupport' && adminSettings.featureSettings.dataPortalCustomerSupport && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Home} label={t('navigationTabs.home')} />
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Users} label={t('navigationTabs.users')} />
                    <StyledTab value={PortalTabs.Troubleshooting} label={t('navigationTabs.troubleshooting')} />
                    <StyledTab value={PortalTabs.AskTheAI} label={t('navigationTabs.asktheai')} />
                    {false && <StyledTab value={PortalTabs.Insights} label={t('navigationTabs.insights')} />}
                    {false && <StyledTab value={PortalTabs.Feedback} label={t('navigationTabs.feedback')} />}
                </StyledTabs>
            )}
            {authMode === 'classBusiness' && adminSettings.featureSettings.dataPortalClassBusinessAnalyst && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Users} label={t('navigationTabs.users')} />
                    <StyledTab value={PortalTabs.Insights} label={t('navigationTabs.insights')} />
                    <StyledTab value={PortalTabs.Settings} label={t('navigationTabs.settings')} />
                    <StyledTab value={PortalTabs.Data} label={t('navigationTabs.data')} />
                </StyledTabs>
            )}
            {authMode === 'customerBusiness' && adminSettings.featureSettings.dataPortalBusinessAnalyst && (
                <StyledTabs
                    value={activeTab}
                    onChange={(_event, value) => handleChange(value)}
                    orientation="vertical"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <StyledTab value={PortalTabs.Classes} label={t('navigationTabs.classes')} />
                    <StyledTab value={PortalTabs.Users} label={t('navigationTabs.users')} />
                    <StyledTab value={PortalTabs.Insights} label={t('navigationTabs.insights')} />
                    <StyledTab value={PortalTabs.Settings} label={t('navigationTabs.settings')} />
                    <StyledTab value={PortalTabs.Data} label={t('navigationTabs.data')} />
                </StyledTabs>
            )}
            <BottomControls>
                <StyledButton fullWidth onClick={logout}>
                    <Icon path={mdiLogout} />
                    {t('auth.logout')}
                </StyledButton>
            </BottomControls>
        </NavContainer>
    );
};
export default PortalNav;
